'use client';

import type { ProductConfId, ProductDetail } from '@eliadmin/core/src/api/proform/proform-types';
import { formatPreviewDimensions } from '@eliadmin/core/src/components/configurator/configurator-preview-copy';
import {
  getProformGlassMaterialLabel,
  getProformGlassMaterialNote,
  getProformGlassPreviewCopy,
  type ProformGlassBackground,
} from '@eliadmin/core/src/components/configurator/proform-glass-preview-copy';
import {
  ProformModelViewer,
  type ProformFrontFrame,
} from '@eliadmin/core/src/components/configurator/proform-model-viewer';
import {
  getProform2dPreviewMode,
  getProformPreviewRuntimeAssetSet,
  isProformPreviewHandleCompatible,
  resolveProformPreviewConfiguration,
} from '@eliadmin/core/src/components/configurator/proform-preview-assets';
import type { ProformHandleDescriptor } from '@eliadmin/core/src/components/configurator/proform-preview-assets';
import { useConfiguratorStore } from '@eliadmin/core/src/stores/configurator-store';
import { useLocaleStore } from '@eliadmin/core/src/stores/locale-store';
import { getProformEdgePreviewCopy, PROFORM_EDGE_SIDES } from './proform-edge-preview-copy';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { RiBox3Line, RiFullscreenExitLine, RiFullscreenLine, RiRuler2Line } from 'react-icons/ri';

type PreviewMode = '3d' | '2d';

type ProformConfiguratorPreviewProps = {
  className?: string;
  productConfId: ProductConfId;
  productDetail: ProductDetail;
};

const previewModeButtonClassName =
  'inline-flex h-8 min-w-[52px] items-center justify-center gap-1.5 rounded-full px-2.5 text-[11px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent';
// Proform currently supplies selection thumbnails, not render-ready glass materials.
// The hand-drawn fallback remains neutral; model previews use the mapped glass renderer.
const GENERIC_GLASS_2D_FILL = 'rgba(220, 239, 242, 0.58)';

function fitDrawing(widthMm: number, heightMm: number, envelopeWidthMm = widthMm) {
  const canvas = { width: 500, height: 640 };
  const area = { left: 58, right: 112, top: 78, bottom: 82 };
  const availableWidth = canvas.width - area.left - area.right;
  const availableHeight = canvas.height - area.top - area.bottom;
  const fittedWidthMm = Math.max(widthMm, envelopeWidthMm);
  const ratio = Math.max(fittedWidthMm / Math.max(heightMm, 1), 0.01);
  const envelopeWidth = Math.min(availableWidth, availableHeight * ratio);
  const scale = envelopeWidth / Math.max(fittedWidthMm, 1);
  const width = widthMm * scale;
  const height = heightMm * scale;
  return {
    ...canvas,
    drawing: {
      height,
      left: area.left + (availableWidth - width) / 2,
      top: area.top + (availableHeight - height) / 2,
      width,
    },
  };
}

function DoorPattern({
  dimensionPattern,
  drawing,
  surfaceFill,
  usesFilling,
}: {
  dimensionPattern?: 'w030-p' | 'w030-xmf8';
  drawing: ReturnType<typeof fitDrawing>['drawing'];
  surfaceFill: string;
  usesFilling: boolean;
}) {
  const { height, left, top, width } = drawing;
  const outerRadius = Math.min(1.5, Math.min(width, height) * 0.006);
  const outlineColor = '#786e63';

  if (dimensionPattern === 'w030-xmf8') {
    const sideStile = Math.max(width * 0.165, 8);
    const topRail = Math.max(height * 0.041, 5);
    const bottomRail = Math.max(height * 0.054, 5);
    const innerLeft = left + sideStile;
    const innerTop = top + topRail;
    const innerWidth = Math.max(width - sideStile * 2, 4);
    const innerHeight = Math.max(height - topRail - bottomRail, 4);
    const upperOpeningHeight = innerHeight * 0.225;
    const transomHeight = Math.max(height * 0.04, 4);
    const lowerTop = innerTop + upperOpeningHeight + transomHeight;
    const lowerHeight = Math.max(innerTop + innerHeight - lowerTop, 4);
    const centerMullionWidth = Math.max(width * 0.045, 3);
    const rowRailHeight = Math.max(height * 0.012, 1.5);
    const braceWidth = Math.max(height * 0.022, 2.5);
    return (
      <g>
        <rect
          x={left}
          y={top}
          width={width}
          height={height}
          rx={outerRadius}
          fill={surfaceFill}
          stroke={outlineColor}
          strokeWidth="1.15"
        />
        <rect
          x={innerLeft}
          y={innerTop}
          width={innerWidth}
          height={upperOpeningHeight}
          fill={usesFilling ? GENERIC_GLASS_2D_FILL : '#f7f7f5'}
          stroke="#8c8175"
          strokeWidth="0.8"
        />
        <line
          x1={innerLeft}
          y1={innerTop}
          x2={innerLeft + innerWidth}
          y2={innerTop + upperOpeningHeight}
          stroke={outlineColor}
          strokeWidth={braceWidth + 1.4}
        />
        <line
          x1={innerLeft}
          y1={innerTop + upperOpeningHeight}
          x2={innerLeft + innerWidth}
          y2={innerTop}
          stroke={outlineColor}
          strokeWidth={braceWidth + 1.4}
        />
        <line
          x1={innerLeft}
          y1={innerTop}
          x2={innerLeft + innerWidth}
          y2={innerTop + upperOpeningHeight}
          stroke={surfaceFill}
          strokeWidth={braceWidth}
        />
        <line
          x1={innerLeft}
          y1={innerTop + upperOpeningHeight}
          x2={innerLeft + innerWidth}
          y2={innerTop}
          stroke={surfaceFill}
          strokeWidth={braceWidth}
        />
        <rect
          x={innerLeft}
          y={lowerTop}
          width={innerWidth}
          height={lowerHeight}
          fill={usesFilling ? GENERIC_GLASS_2D_FILL : '#f7f7f5'}
          stroke="#8c8175"
          strokeWidth="0.8"
        />
        <rect
          x={innerLeft + innerWidth / 2 - centerMullionWidth / 2}
          y={lowerTop}
          width={centerMullionWidth}
          height={lowerHeight}
          fill={surfaceFill}
          stroke={outlineColor}
          strokeWidth="0.65"
        />
        {[1, 2, 3].map(row => {
          const railY = lowerTop + (lowerHeight * row) / 4 - rowRailHeight / 2;
          return (
            <rect
              key={row}
              x={innerLeft}
              y={railY}
              width={innerWidth}
              height={rowRailHeight}
              fill={surfaceFill}
              stroke={outlineColor}
              strokeWidth="0.55"
            />
          );
        })}
      </g>
    );
  }

  if (dimensionPattern !== 'w030-p') {
    const frameInsetX = Math.max(width * 0.12, 7);
    const frameInsetY = Math.max(Math.min(width * 0.12, height * 0.075), 7);
    return (
      <g>
        <rect
          x={left}
          y={top}
          width={width}
          height={height}
          rx={outerRadius}
          fill={surfaceFill}
          stroke={outlineColor}
          strokeWidth="1.15"
        />
        {usesFilling && (
          <rect
            x={left + frameInsetX}
            y={top + frameInsetY}
            width={Math.max(width - frameInsetX * 2, 4)}
            height={Math.max(height - frameInsetY * 2, 4)}
            rx={Math.min(0.75, outerRadius / 2)}
            fill={GENERIC_GLASS_2D_FILL}
            stroke="#8c8175"
            strokeWidth="0.9"
          />
        )}
      </g>
    );
  }

  const sideStile = Math.max(width * 0.08, 8);
  const edgeRail = Math.max(height * 0.055, 5);
  const innerLeft = left + sideStile;
  const innerTop = top + edgeRail;
  const innerWidth = Math.max(width - sideStile * 2, 4);
  const innerHeight = Math.max(height - edgeRail * 2, 4);

  return (
    <g>
      <rect
        x={left}
        y={top}
        width={width}
        height={height}
        rx={outerRadius}
        fill={surfaceFill}
        stroke={outlineColor}
        strokeWidth="1.15"
      />
      <rect
        x={innerLeft}
        y={innerTop}
        width={innerWidth}
        height={innerHeight}
        rx={Math.min(0.75, outerRadius / 2)}
        fill={surfaceFill}
        stroke="#8c8175"
        strokeWidth="0.9"
      />
      <rect
        x={innerLeft + 1.5}
        y={innerTop + 1.5}
        width={Math.max(innerWidth - 3, 1)}
        height={Math.max(innerHeight - 3, 1)}
        fill="none"
        stroke="#a09589"
        strokeWidth="0.65"
      />
    </g>
  );
}

function ProformDimensionPreview({
  depthMm,
  dimensionPattern,
  finishColor,
  finishRotationRadians,
  finishTextureUrl,
  handle,
  heightMm,
  modelFront,
  usesFilling,
  widthMm,
}: {
  depthMm: number;
  dimensionPattern?: 'w030-p' | 'w030-xmf8';
  finishColor?: string;
  finishRotationRadians: number;
  finishTextureUrl?: string;
  handle?: ProformHandleDescriptor;
  heightMm: number;
  modelFront: boolean;
  usesFilling: boolean;
  widthMm: number;
}) {
  const compatibleHandle =
    handle && isProformPreviewHandleCompatible(handle, widthMm) ? handle : undefined;
  const renderedHandleLengthMm = compatibleHandle
    ? compatibleHandle.lengthMode === 'fit-edge'
      ? widthMm
      : compatibleHandle.lengthMm
    : 0;
  const { drawing, height, width } = fitDrawing(
    widthMm,
    heightMm,
    Math.max(widthMm, renderedHandleLengthMm),
  );
  const svgId = `proform-${useId().replaceAll(':', '')}`;
  const handleWidth = compatibleHandle ? (renderedHandleLengthMm / widthMm) * drawing.width : 0;
  const handleHeight = compatibleHandle
    ? Math.max((compatibleHandle.nativeSizeMm.profileHeight / heightMm) * drawing.height, 2.5)
    : 0;
  const handleSeatOffset = compatibleHandle
    ? handleHeight *
      Math.min(
        Math.max(
          -compatibleHandle.mountingSeatYMm /
            Math.max(compatibleHandle.nativeSizeMm.profileHeight, 0.001),
          0,
        ),
        1,
      )
    : 0;
  const handleGripWidth = compatibleHandle
    ? Math.min(
        handleWidth,
        (234 / Math.max(compatibleHandle.nativeSizeMm.length, 0.001)) * handleWidth,
      )
    : 0;
  const handleRight = drawing.left + drawing.width / 2 + handleWidth / 2;
  const rightDimensionX = Math.max(drawing.left + drawing.width, handleRight) + 56;
  const topDimensionY = drawing.top - 42;
  const textureTileSize = Math.max(Math.min(drawing.width * 0.45, 140), 64);
  const surfaceFill = finishColor ?? (finishTextureUrl ? `url(#${svgId}-surface)` : '#b8a58e');
  return (
    <div
      className={cn(
        'relative h-full min-h-[360px] w-full overflow-hidden',
        modelFront ? 'pointer-events-none bg-transparent' : 'bg-[#f7f7f5]',
      )}
      style={
        modelFront
          ? undefined
          : {
              backgroundImage:
                'linear-gradient(rgba(31,41,55,0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(31,41,55,0.05) 1px, transparent 1px)',
              backgroundSize: '24px 24px',
            }
      }
    >
      <svg
        viewBox={`0 0 ${width} ${height}`}
        className="h-full w-full"
        role="img"
        aria-label={`Technický 2D náhled, šířka ${widthMm} milimetrů, výška ${heightMm} milimetrů, tloušťka ${depthMm} milimetrů`}
      >
        <defs>
          <marker
            id={`${svgId}-dimension-arrow`}
            markerWidth="8"
            markerHeight="8"
            refX="4"
            refY="4"
            orient="auto-start-reverse"
          >
            <path d="M 0 4 L 8 0 L 8 8 Z" fill="#171717" />
          </marker>
          <filter id={`${svgId}-door-shadow`} x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="3" stdDeviation="3" floodOpacity="0.11" />
          </filter>
          {finishTextureUrl && !finishColor && (
            <pattern
              id={`${svgId}-surface`}
              width={textureTileSize}
              height={textureTileSize}
              patternUnits="userSpaceOnUse"
              patternTransform={`rotate(${(finishRotationRadians * 180) / Math.PI} ${drawing.left + drawing.width / 2} ${drawing.top + drawing.height / 2})`}
            >
              <image
                href={finishTextureUrl}
                width={textureTileSize}
                height={textureTileSize}
                preserveAspectRatio="xMidYMid slice"
              />
            </pattern>
          )}
          <linearGradient id={`${svgId}-handle-metal`} x1="0" y1="0" x2="0" y2="1">
            <stop offset="0" stopColor="#edf0f2" />
            <stop offset="0.45" stopColor="#aeb4b8" />
            <stop offset="1" stopColor="#737b80" />
          </linearGradient>
        </defs>

        {!modelFront && (
          <g filter={`url(#${svgId}-door-shadow)`}>
            <DoorPattern
              dimensionPattern={dimensionPattern}
              drawing={drawing}
              surfaceFill={surfaceFill}
              usesFilling={usesFilling}
            />
          </g>
        )}

        {compatibleHandle && !modelFront && (
          <g
            aria-label={`${compatibleHandle.label}, na horní hraně, délka náhledu ${renderedHandleLengthMm} milimetrů`}
          >
            <rect
              x={drawing.left + drawing.width / 2 - handleWidth / 2}
              y={drawing.top - handleSeatOffset}
              width={handleWidth}
              height={handleHeight}
              rx={Math.min(handleHeight / 2, 1.5)}
              fill={`url(#${svgId}-handle-metal)`}
              stroke="#667076"
              strokeWidth="0.65"
            />
            <rect
              x={drawing.left + drawing.width / 2 - handleGripWidth / 2}
              y={drawing.top}
              width={handleGripWidth}
              height={Math.max(handleHeight - handleSeatOffset, 1.4)}
              rx={Math.min(handleHeight / 2, 1.5)}
              fill="#70787d"
              opacity="0.7"
            />
          </g>
        )}

        <g stroke="#171717" strokeWidth="1.5" fill="none">
          <line
            x1={drawing.left}
            y1={drawing.top}
            x2={drawing.left}
            y2={topDimensionY - 8}
            strokeDasharray="5 5"
            opacity="0.55"
          />
          <line
            x1={drawing.left + drawing.width}
            y1={drawing.top}
            x2={drawing.left + drawing.width}
            y2={topDimensionY - 8}
            strokeDasharray="5 5"
            opacity="0.55"
          />
          <line
            x1={drawing.left + 4}
            y1={topDimensionY}
            x2={drawing.left + drawing.width - 4}
            y2={topDimensionY}
            markerStart={`url(#${svgId}-dimension-arrow)`}
            markerEnd={`url(#${svgId}-dimension-arrow)`}
          />
          <line
            x1={drawing.left + drawing.width}
            y1={drawing.top}
            x2={rightDimensionX + 8}
            y2={drawing.top}
            strokeDasharray="5 5"
            opacity="0.55"
          />
          <line
            x1={drawing.left + drawing.width}
            y1={drawing.top + drawing.height}
            x2={rightDimensionX + 8}
            y2={drawing.top + drawing.height}
            strokeDasharray="5 5"
            opacity="0.55"
          />
          <line
            x1={rightDimensionX}
            y1={drawing.top + 4}
            x2={rightDimensionX}
            y2={drawing.top + drawing.height - 4}
            markerStart={`url(#${svgId}-dimension-arrow)`}
            markerEnd={`url(#${svgId}-dimension-arrow)`}
          />
        </g>

        <g fontFamily="inherit" fontWeight="700" fontSize="15" fill="#171717">
          <rect
            x={drawing.left + drawing.width / 2 - 48}
            y={topDimensionY - 25}
            width="96"
            height="24"
            rx="12"
            fill="white"
            stroke="#dedbd6"
          />
          <text x={drawing.left + drawing.width / 2} y={topDimensionY - 8} textAnchor="middle">
            {widthMm} mm
          </text>
          <g
            transform={`translate(${rightDimensionX + 29} ${drawing.top + drawing.height / 2}) rotate(90)`}
          >
            <rect x="-48" y="-18" width="96" height="24" rx="12" fill="white" stroke="#dedbd6" />
            <text x="0" y="-1" textAnchor="middle">
              {heightMm} mm
            </text>
          </g>
        </g>

        <g transform={`translate(${drawing.left} ${drawing.top + drawing.height + 34})`}>
          <rect width="148" height="30" rx="15" fill="white" stroke="#dedbd6" />
          <text
            x="74"
            y="20"
            textAnchor="middle"
            fontFamily="inherit"
            fontSize="13"
            fontWeight="700"
            fill="#4b5563"
          >
            Tloušťka {depthMm} mm
          </text>
        </g>
      </svg>
    </div>
  );
}

export function ProformConfiguratorPreview({
  className,
  productConfId,
  productDetail,
}: ProformConfiguratorPreviewProps) {
  const [mode, setMode] = useState<PreviewMode>('3d');
  const [glassBackground, setGlassBackground] = useState<ProformGlassBackground>('logo');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const previewRef = useRef<HTMLElement>(null);
  const productCode = useConfiguratorStore(state => state.cislo_nomenklatury);
  const productConf = useConfiguratorStore(state => state.productConf);
  const locale = useLocaleStore(state => state.currentLocale);
  const glassCopy = getProformGlassPreviewCopy(locale);
  const edgeCopy = getProformEdgePreviewCopy(locale);
  const assetSet = getProformPreviewRuntimeAssetSet(productCode);

  const preview = useMemo(
    () =>
      assetSet
        ? resolveProformPreviewConfiguration(assetSet, productConfId, productDetail, productConf)
        : null,
    [assetSet, productConf, productConfId, productDetail],
  );

  useEffect(() => {
    const updateFullscreenState = () => {
      setIsFullscreen(document.fullscreenElement === previewRef.current);
    };

    document.addEventListener('fullscreenchange', updateFullscreenState);
    updateFullscreenState();
    return () => document.removeEventListener('fullscreenchange', updateFullscreenState);
  }, []);

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement === previewRef.current) {
        await document.exitFullscreen();
      } else {
        await previewRef.current?.requestFullscreen?.();
      }
    } catch {
      setIsFullscreen(document.fullscreenElement === previewRef.current);
    }
  };

  if (!assetSet || !preview) return null;

  const dimensionsText = formatPreviewDimensions(preview, locale);

  const activeHandle =
    preview.hasConfiguredHandleSelection &&
    preview.handle &&
    isProformPreviewHandleCompatible(preview.handle, preview.widthMm)
      ? preview.handle
      : null;

  const activeHandleRenderedLengthMm = activeHandle
    ? activeHandle.lengthMode === 'fit-edge'
      ? preview.widthMm
      : activeHandle.lengthMm
    : undefined;
  const activeHandleLengthLabel = activeHandle
    ? activeHandle.lengthMode === 'fit-edge'
      ? `náhled ${activeHandleRenderedLengthMm} mm přes celou šířku`
      : `náhled ${activeHandleRenderedLengthMm} mm`
    : undefined;
  const usesModelFront2d = getProform2dPreviewMode(preview.model) === 'model-front';
  const frontLayout = fitDrawing(
    preview.widthMm,
    preview.heightMm,
    Math.max(preview.widthMm, activeHandleRenderedLengthMm ?? 0),
  );
  const frontFrame: ProformFrontFrame | undefined = usesModelFront2d
    ? {
        drawing: frontLayout.drawing,
        viewBoxHeight: frontLayout.height,
        viewBoxWidth: frontLayout.width,
      }
    : undefined;
  const showsModelViewer = mode === '3d' || usesModelFront2d;
  const glassSelection = preview.usesFilling
    ? {
        groupCode: preview.fillingGroupCode,
        fillingCode: preview.fillingCode,
        orientation: preview.fillingOrientation,
        ornamentSide: preview.fillingOrnamentSide,
        color: preview.fillingColor,
      }
    : undefined;

  const layerClassName = (active: boolean) =>
    active
      ? 'absolute inset-0 visible opacity-100'
      : 'pointer-events-none invisible absolute inset-0 opacity-0';

  return (
    <section
      ref={previewRef}
      className={cn(
        'relative flex min-h-[400px] w-full flex-col overflow-hidden rounded-xl border border-border bg-background shadow-sm [&:fullscreen]:min-h-screen [&:fullscreen]:rounded-none [&:fullscreen]:border-0',
        className,
      )}
      aria-label="Náhled konfigurace produktu"
      data-proform-product-preview={productCode}
      data-proform-execution-group={preview.executionGroupCode}
      data-proform-execution={preview.executionCode}
      data-proform-model-preview-image={preview.model.previewImagePath ?? 'none'}
      data-proform-handle-preview={activeHandle?.id ?? 'none'}
      data-proform-handle-rendered-length={activeHandleRenderedLengthMm}
      data-proform-supports-glass={preview.supportsGlass}
      data-proform-glass-background={preview.supportsGlass ? glassBackground : 'none'}
    >
      <div className="flex items-start justify-between gap-3 border-b border-border bg-background px-3 py-2.5">
        <div className="min-w-0 flex-1">
          <p className="truncate text-xs font-semibold text-foreground">{preview.model.label}</p>
          <p className="truncate text-[11px] text-muted-foreground" title={dimensionsText}>
            {dimensionsText}
          </p>
        </div>
        <div
          className="flex shrink-0 rounded-full bg-muted p-0.5"
          role="tablist"
          aria-label="Typ náhledu"
        >
          <button
            type="button"
            role="tab"
            aria-selected={mode === '3d'}
            className={cn(
              previewModeButtonClassName,
              mode === '3d' ? 'bg-background text-accent shadow-sm' : 'text-muted-foreground',
            )}
            onClick={() => setMode('3d')}
          >
            <RiBox3Line size={14} /> 3D
          </button>
          <button
            type="button"
            role="tab"
            aria-selected={mode === '2d'}
            className={cn(
              previewModeButtonClassName,
              mode === '2d' ? 'bg-background text-accent shadow-sm' : 'text-muted-foreground',
            )}
            onClick={() => setMode('2d')}
          >
            <RiRuler2Line size={14} /> 2D
          </button>
        </div>
      </div>

      <div className="relative min-h-[360px] flex-1">
        <button
          type="button"
          className="absolute right-3 top-3 z-20 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-background/90 text-muted-foreground shadow-sm backdrop-blur transition hover:border-accent hover:text-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
          onClick={toggleFullscreen}
          aria-label={isFullscreen ? 'Ukončit celou obrazovku' : 'Celá obrazovka'}
          aria-pressed={isFullscreen}
          title={isFullscreen ? 'Ukončit celou obrazovku' : 'Celá obrazovka'}
        >
          {isFullscreen ? <RiFullscreenExitLine size={16} /> : <RiFullscreenLine size={16} />}
        </button>
        <div className={layerClassName(showsModelViewer)} aria-hidden={!showsModelViewer}>
          <ProformModelViewer
            depthMm={preview.depthMm}
            edges={assetSet.productCode === 'NDALVICZENIT' ? preview.edges : undefined}
            finishColor={preview.finishColor}
            finishRotationRadians={preview.finishRotationRadians}
            finishTextureUrl={preview.finishTextureUrl}
            frontFrame={frontFrame}
            glassBackground={preview.supportsGlass ? glassBackground : 'none'}
            glassSelection={glassSelection}
            handle={activeHandle ?? undefined}
            heightMm={preview.heightMm}
            isActive={showsModelViewer}
            model={preview.model}
            presentation={mode === '2d' && usesModelFront2d ? 'front-2d' : 'interactive-3d'}
            usesFilling={preview.usesFilling}
            widthMm={preview.widthMm}
          />
        </div>
        <div className={layerClassName(mode === '2d')} aria-hidden={mode !== '2d'}>
          <ProformDimensionPreview
            depthMm={preview.depthMm}
            dimensionPattern={preview.model.dimensionPattern}
            finishColor={preview.finishColor}
            finishRotationRadians={preview.finishRotationRadians}
            finishTextureUrl={preview.finishTextureUrl}
            handle={activeHandle ?? undefined}
            heightMm={preview.heightMm}
            modelFront={usesModelFront2d}
            usesFilling={preview.usesFilling}
            widthMm={preview.widthMm}
          />
        </div>
      </div>

      <div className="grid max-h-[40dvh] shrink-0 gap-1.5 overflow-y-auto border-t border-border bg-background px-3 py-2 text-[11px] leading-5">
        {preview.supportsGlass && (
          <div className="order-first grid gap-1.5 border-b border-border pb-2">
            <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1.5">
              {glassSelection && (
                <p className="font-semibold text-foreground">
                  {getProformGlassMaterialLabel(locale, glassSelection)}
                </p>
              )}
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[10px] text-muted-foreground">{glassCopy.background}</span>
                <div
                  className="inline-flex rounded-full bg-muted p-0.5"
                  role="group"
                  aria-label={glassCopy.background}
                >
                  {(['none', 'logo', 'flowers'] as const).map(background => (
                    <button
                      key={background}
                      type="button"
                      aria-pressed={glassBackground === background}
                      onClick={() => setGlassBackground(background)}
                      className={cn(
                        'rounded-full px-2 py-0.5 text-[10px] font-semibold transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent',
                        glassBackground === background
                          ? 'bg-background text-accent shadow-sm'
                          : 'text-muted-foreground hover:text-foreground',
                      )}
                    >
                      {glassCopy.backgrounds[background]}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            <p
              className="text-[10px] leading-4 text-muted-foreground"
              role={!glassSelection ? 'status' : undefined}
            >
              {glassSelection
                ? getProformGlassMaterialNote(locale, glassSelection)
                : glassCopy.chooseFilling}
            </p>
          </div>
        )}
        <div className="flex min-w-0 items-center gap-2">
          {(preview.finishColor || preview.finishPreviewUrl) && (
            <span
              className="h-7 w-7 shrink-0 rounded-md border border-border bg-cover bg-center"
              style={
                preview.finishColor
                  ? { backgroundColor: preview.finishColor }
                  : {
                      backgroundImage: `url(${JSON.stringify(preview.finishPreviewUrl).slice(1, -1)})`,
                    }
              }
              aria-hidden="true"
            />
          )}
          <p className="truncate font-semibold text-foreground" title={preview.finishLabel}>
            {preview.finishCode && `${preview.finishCode} · `}
            {preview.finishLabel}
          </p>
        </div>
        <p
          className="truncate text-muted-foreground"
          title={[preview.executionGroupLabel, preview.executionLabel].join(' · ')}
        >
          Provedení: {preview.executionGroupCode} / {preview.executionCode} ·{' '}
          {preview.executionGroupLabel} / {preview.executionLabel}
        </p>
        {preview.hasConfiguredHandleSelection && (
          <p className="truncate text-muted-foreground" title={activeHandle?.label}>
            Úchytka:{' '}
            {activeHandle
              ? `${activeHandle.label} · ${activeHandleLengthLabel}`
              : edgeCopy.handleUnavailable}
          </p>
        )}
        {PROFORM_EDGE_SIDES.some(side => preview.edges[side]) && (
          <div
            className="grid grid-cols-2 gap-x-3 gap-y-1 border-t border-border pt-1.5"
            aria-label={edgeCopy.edges}
          >
            {PROFORM_EDGE_SIDES.map(side => {
              const edge = preview.edges[side];
              if (!edge) return null;
              return (
                <div
                  key={side}
                  className="flex min-w-0 items-center gap-1.5"
                  data-proform-edge-side={side}
                  data-proform-edge-code={edge.code}
                >
                  {edge.previewUrl && (
                    <span
                      aria-hidden="true"
                      className="h-4 w-6 shrink-0 rounded border border-border bg-cover bg-center"
                      style={{ backgroundImage: `url(${JSON.stringify(edge.previewUrl)})` }}
                    />
                  )}
                  <p className="truncate text-muted-foreground" title={edge.label}>
                    {edgeCopy.sides[side]}: {edge.code}
                  </p>
                </div>
              );
            })}
          </div>
        )}
        {(preview.fillingCode || preview.fillingGroupCode) && (
          <div className="flex min-w-0 items-center gap-2">
            {preview.fillingPreviewUrl && (
              <span
                className="h-7 w-7 shrink-0 rounded-md border border-border bg-cover bg-center"
                style={{
                  backgroundColor: '#e8eeef',
                  backgroundImage: `url(${JSON.stringify(preview.fillingPreviewUrl).slice(1, -1)})`,
                }}
                aria-hidden="true"
              />
            )}
            <p
              className="truncate text-muted-foreground"
              title={[preview.fillingGroupLabel, preview.fillingLabel].filter(Boolean).join(' · ')}
            >
              Výplň: {[preview.fillingGroupCode, preview.fillingCode].filter(Boolean).join(' / ')}
              {(preview.fillingGroupLabel || preview.fillingLabel) && ' · '}
              {[preview.fillingGroupLabel, preview.fillingLabel].filter(Boolean).join(' / ')}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
